import React, { useEffect, useRef, useState, useId } from 'react';
import { createPortal } from 'react-dom';
import { Building2, Search, X, ChevronRight, Plus, Check } from 'lucide-react';
import { DataService } from '../../services/data-service';
import { useModalAccessibility } from '../../utils/use-modal-accessibility';
import './client-match.css';

type Client = { id: string; name: string; abn?: string; companyType?: string; [key: string]: any };
type Candidate = { company: Client; reasons: string[]; confidence: number; summary?: { contactName: string; contactEmail: string | null; quoteCount: number; siteCount: number } };
/** A building (owners corporation) with no firm that matched the request. It is not a client and cannot be picked. */
export type ClientMatchBuilding = { company?: { id: string; name?: string } | null; id?: string; name?: string; reasons?: string[] };
/**
 * What the server already found for this request (POST /projects/:id/crm-link/resolve,
 * state 'ask'): firms only, the unmanaged buildings apart, and the firm the requester
 * email names when the Quote Request names another (SPEC 2026-10-08 D4, D5).
 */
/**
 * One piece of the RFQ's evidence and the firm it names (decision E5,
 * 2026-10-13): the requester email, a firm or ABN printed on it, a named
 * person in Clients, the strata plan's building, a learned RFQ layout.
 * `conflict` lists the firms when one piece names more than one.
 */
export type ClientMatchSignal = {
    source?: string;
    evidence?: string;
    label?: string;
    company?: { id: string; name?: string | null } | null;
    conflict?: Array<{ id: string; name?: string | null }> | null;
};
export type ClientMatchSeed = {
    suggestedFirm?: Client | null;
    pdfFirmName?: string | null;
    candidates?: Candidate[];
    unmanagedBuildings?: ClientMatchBuilding[];
    signals?: ClientMatchSignal[];
};
/** The request's own evidence, shown above the search (D5). */
export type ClientMatchSource = { email?: string; pdfFirmName?: string; plan?: string };
export type ClientMatchDraft = { name: string; email?: string; phone?: string; abn?: string; companyType?: string; [key: string]: any };
interface Props {
    draft: ClientMatchDraft;
    context?: string;
    footerHint?: string;
    closeLabel?: string;
    sourceLabel?: string;
    seed?: ClientMatchSeed | null;
    source?: ClientMatchSource | null;
    /** The job's plan ("SP2068"): a new client then defaults to Strata management. */
    planNumber?: string;
    /** False on a job's prompt: a building is never a job's client (D4). */
    offerOwnersCorporation?: boolean;
    /**
     * True on a job's prompt: every search asks POST /companies/match for firms
     * only (`firms: true`, with the job's plan), the same list the server's
     * answer gave, so a building row is never offered as the client and a
     * building with no firm is listed apart (D4). Clients and intake keep the
     * identity search, which lists every row (I5).
     */
    firmsOnly?: boolean;
    /** Opens Tidy up from the unmanaged-building note. */
    onOpenTidy?: () => void;
    onChoose: (client: Client, created: boolean) => Promise<void>;
    onClose: () => void;
}

const COMPANY_TYPES: Array<[string, string]> = [
    ['strata_manager', 'Strata management'], ['body_corporate', 'Owners corporation'], ['homeowner', 'Homeowner'],
    ['property_developer', 'Developer'], ['council', 'Council'], ['insurance', 'Insurance'], ['real_estate', 'Real estate'], ['other', 'Other'],
];

// The ranker's reasons, as plain words. A reason the server words itself
// ("Contact on The Owners - SP 2068") passes through unchanged.
const REASON_WORDS: Record<string, string> = {
    'Same ABN': 'Same ABN',
    'Same company name': 'Same name',
    'Similar company name': 'Similar name',
    'Company name starts with the search': 'Name starts with the search',
    'Same contact email': 'A contact has this email',
    'Same company email domain': 'Its staff use this email domain',
    'Same contact phone': 'A contact has this phone number',
    'Same strata plan': 'Same strata plan',
    'Different ABN — check before matching': 'Different ABN: check before choosing',
};
export function plainReasons(reasons: readonly string[] = []): string {
    const words = reasons.map(reason => REASON_WORDS[reason] ?? reason.replace(/\s+[—–]\s+/g, ': '));
    return [...new Set(words)].join(' · ');
}

const sameName = (a?: string | null, b?: string | null) => {
    const norm = (value?: string | null) => String(value || '').toLowerCase().replace(/\b(?:pty\s*\.?\s*ltd|p\/l|limited)\.?\b/g, '').replace(/[^a-z0-9]+/g, '');
    return !!norm(a) && norm(a) === norm(b);
};
const buildingOf = (entry: ClientMatchBuilding) => ({ id: entry.company?.id ?? entry.id ?? '', name: String(entry.company?.name ?? entry.name ?? '').trim(), reasons: entry.reasons || [] });
/** Why a building with no firm came up, in the words of the note under the list. */
function unmanagedWhy(reasons: readonly string[]): string {
    if (reasons.includes('Same contact email')) return 'holds this email';
    if (reasons.includes('Same company email domain')) return 'has a contact with this email domain';
    if (reasons.includes('Same contact phone')) return 'holds this phone number';
    if (reasons.includes('Same strata plan')) return 'is this strata plan';
    return 'matches this request';
}
const planText = (plan?: string) => (plan ? plan.replace(/^SP\s*/i, 'SP ') : '');

/**
 * The opening search merged with the server's answer: each client once, with
 * the reasons of both and the higher confidence, highest first (ties keep the
 * search's order).
 */
function mergeCandidates(found: readonly Candidate[], seeded: readonly Candidate[]): Candidate[] {
    const merged = new Map<string, Candidate>();
    for (const candidate of [...found, ...seeded]) {
        const id = candidate.company.id;
        const prior = merged.get(id);
        merged.set(id, prior ? {
            ...prior,
            reasons: [...new Set([...(prior.reasons || []), ...(candidate.reasons || [])])],
            confidence: Math.max(prior.confidence, candidate.confidence),
            summary: prior.summary ?? candidate.summary,
        } : candidate);
    }
    return [...merged.values()].sort((a, b) => b.confidence - a.confidence);
}
function mergeBuildings(found: readonly ClientMatchBuilding[], seeded: readonly ClientMatchBuilding[]): ClientMatchBuilding[] {
    const seen = new Set(found.map(entry => buildingOf(entry).id || buildingOf(entry).name));
    return [...found, ...seeded.filter(entry => !seen.has(buildingOf(entry).id || buildingOf(entry).name))];
}

/** All company creation surfaces use the same explicit review. Searching is read-only. */
export default function ClientMatchDialog({ draft, context, footerHint = 'Your quote is saved even if you match the client later.', closeLabel = 'Match later', sourceLabel = 'From this request', seed = null, source = null, planNumber = '', offerOwnersCorporation = true, firmsOnly = false, onOpenTidy, onChoose, onClose }: Props) {
    const initialQuery = draft.name || '';
    const [query, setQuery] = useState(initialQuery);
    const [candidates, setCandidates] = useState<Candidate[]>(() => seed?.candidates || []);
    const [buildings, setBuildings] = useState<ClientMatchBuilding[]>(() => seed?.unmanagedBuildings || []);
    // The server's answer covers the opening search, except a contact phone:
    // resolve ranks the email, the names and the plan only. With a phone the
    // opening search still runs, as before the server answer existed, and the
    // answer is merged into it, so a client known only by its phone stays listed.
    const mergeSeed = !!seed && !!draft.phone;
    const [loading, setLoading] = useState(!seed || mergeSeed);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [errorStage, setErrorStage] = useState<'search' | 'save'>('search');
    const [creating, setCreating] = useState(false);
    const [name, setName] = useState(draft.name || '');
    const [type, setType] = useState(() => {
        const wanted = draft.companyType || draft.company_type || (planNumber ? 'strata_manager' : 'other');
        return !offerOwnersCorporation && wanted === 'body_corporate' ? 'strata_manager' : wanted;
    });
    const [retry, setRetry] = useState(0);
    const container = useRef<HTMLDivElement>(null);
    const mounted = useRef(true);
    const closeRef = useRef(onClose);
    closeRef.current = onClose;
    const busyRef = useRef(false);
    const committedChoice = useRef<{ key: string; client: Client; created: boolean } | null>(null);
    const lastCandidate = useRef<Client | undefined>(undefined);
    // Without a phone the server's answer is the opening search; it is not repeated.
    const seededQuery = useRef<string | null>(seed && !mergeSeed ? initialQuery : null);
    const headingId = useId();
    const modalProps = useModalAccessibility(container, () => { if (!busyRef.current) closeRef.current(); }, true);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

    useEffect(() => {
        if (seededQuery.current !== null && query === seededQuery.current && retry === 0) return undefined;
        seededQuery.current = null;
        // The server searches by a name or an email and refuses a search with
        // neither (400 "name is required"). A strata job with no email and no
        // firm name starts with an empty search (D5): nothing is sent, the
        // server's answer (if any) shows, and Create a new client stays open.
        if (!query.trim() && !draft.email) {
            setError('');
            setCandidates(seed?.candidates || []);
            setBuildings(seed?.unmanagedBuildings || []);
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        const withSeed = mergeSeed && query === initialQuery ? seed : null;
        setLoading(true);
        setError('');
        const timer = setTimeout(async () => {
            try {
                const result = await (DataService.companies as any).match({
                    name: query.trim(), email: draft.email, phone: draft.phone, abn: draft.abn,
                    ...(firmsOnly ? { firms: true, ...(planNumber ? { plan: planNumber } : {}) } : {}),
                });
                if (!cancelled) {
                    const found: Candidate[] = result.candidates || [];
                    const foundBuildings: ClientMatchBuilding[] = result.unmanagedBuildings || [];
                    setCandidates(withSeed ? mergeCandidates(found, withSeed.candidates || []) : found);
                    setBuildings(withSeed ? mergeBuildings(foundBuildings, withSeed.unmanagedBuildings || []) : foundBuildings);
                }
            } catch (err: any) {
                if (cancelled) return;
                if (withSeed) {
                    // The server's answer still stands; only the phone could not be added.
                    setCandidates(withSeed.candidates || []); setBuildings(withSeed.unmanagedBuildings || []);
                } else {
                    setCandidates([]); setBuildings([]); setErrorStage('search'); setError(err?.message || 'Could not search clients. Please try again.');
                }
            } finally { if (!cancelled) setLoading(false); }
        }, 200);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [query, draft.email, draft.phone, draft.abn, retry, firmsOnly, planNumber]);

    const choose = async (candidate?: Client) => {
        if (busyRef.current || (!candidate && !name.trim())) return;
        busyRef.current = true;
        lastCandidate.current = candidate;
        setBusy(true);
        setError('');
        try {
            const choiceKey = candidate ? `existing:${candidate.id}` : `new:${name.trim().toLowerCase()}:${type}`;
            if (committedChoice.current?.key !== choiceKey) {
                const result = await (DataService.companies as any).findOrCreate(candidate
                    ? { name: candidate.name, companyId: candidate.id }
                    : { ...draft, name: name.trim(), companyType: type, company_type: type, confirmCreate: true });
                if (!result.row?.id) throw new Error('The client could not be confirmed. Please try again.');
                committedChoice.current = { key: choiceKey, client: result.row, created: result.action === 'created' };
            }
            // The parent is keyed by project/tenant and owns its stale-write guard.
            // A later contact/project failure retries that work, not the committed company creation.
            if (!mounted.current) return;
            await onChoose(committedChoice.current.client, committedChoice.current.created);
        } catch (err: any) {
            if (mounted.current) {
                setErrorStage('save');
                setError(err?.message || 'Could not save this client. Please try again.');
                const nextCandidates = err?.body?.candidates || err?.data?.candidates;
                if (nextCandidates) { setCandidates(nextCandidates); setCreating(false); }
            }
        } finally {
            busyRef.current = false;
            if (mounted.current) setBusy(false);
        }
    };

    // The firm the requester email names, first while the opening search shows (D5).
    const suggested = seed?.suggestedFirm?.id ? seed.suggestedFirm : null;
    const shown: Candidate[] = suggested && query === initialQuery
        ? [{ company: suggested, reasons: ['Matches the requester email'], confidence: 1 },
            ...candidates.filter(candidate => candidate.company.id !== suggested.id)]
        : candidates;
    const reasonsOf = (candidate: Candidate) => {
        const words = plainReasons(candidate.reasons);
        return suggested && candidate.company.id === suggested.id && !/requester email/.test(words)
            ? ['Matches the requester email', words].filter(Boolean).join(' · ')
            : words;
    };
    const unmanaged = buildings.map(buildingOf).filter(building => building.name);
    // E5: each piece of evidence and the firm it names, so a disagreement is visible.
    const signals = (seed?.signals || []).filter(signal => signal?.label && (signal.company?.id || signal.conflict?.length));
    const signalFirm = (signal: ClientMatchSignal) => signal.company?.name
        || (signal.conflict || []).map(firm => firm.name).filter(Boolean).join(' or ')
        || 'a client';
    const pdfFirm = source?.pdfFirmName || seed?.pdfFirmName || '';
    const disagree = !!(suggested && pdfFirm && !sameName(suggested.name, pdfFirm));
    const typeOptions = COMPANY_TYPES.filter(([value]) => offerOwnersCorporation || value !== 'body_corporate');

    return createPortal(<div className="crm-match-backdrop">
        <div {...modalProps} aria-labelledby={headingId} className="crm-match-dialog" ref={container} tabIndex={-1}>
            <div className="crm-match-heading">
                <span className="crm-match-icon"><Building2 size={20} aria-hidden="true" /></span>
                <div><h2 id={headingId}>Choose the client</h2><p>{source ? 'The client is the firm that sent this request.' : 'Keep requests and quotes together in one client record.'}</p></div>
                <button type="button" className="crm-match-close" aria-label="Close client matching" disabled={busy} onClick={onClose}><X size={20} /></button>
            </div>
            <div className="crm-match-body">
                {context && <p className="crm-match-context">{context}</p>}
                {source
                    ? (source.email || pdfFirm || source.plan) && <div className="crm-match-source" aria-label="From this request">
                        <span>{sourceLabel}</span>
                        <dl className="crm-match-source-facts">
                            {source.email && <><dt>Requester email</dt><dd>{source.email}</dd></>}
                            {pdfFirm && <><dt>Quote Request names</dt><dd>{pdfFirm}</dd></>}
                            {source.plan && <><dt>Strata plan</dt><dd>{planText(source.plan)}</dd></>}
                        </dl>
                        {disagree && <p className="crm-match-disagree">The requester email belongs to <strong>{suggested!.name}</strong>, but the Quote Request names <strong>{pdfFirm}</strong>. Choose the right client.</p>}
                    </div>
                    : (draft.name || draft.email) && <div className="crm-match-source"><span>{sourceLabel}</span><strong>{draft.name || 'Client name not provided'}</strong>{draft.email && <small>{draft.email}</small>}</div>}
                {signals.length > 0 && <div className="crm-match-signals" role="group" aria-label="What this request points to">
                    <span>What this request points to</span>
                    <ul>{signals.map((signal, index) => <li key={`${signal.source || 'signal'}-${index}`} data-signal={signal.source}>
                        {signal.label}: <strong>{signalFirm(signal)}</strong>
                    </li>)}</ul>
                </div>}
                <label className="crm-match-search"><Search size={18} aria-hidden="true" /><input data-qm-default-focus className="qm-input-unthemed" aria-label="Search existing clients" placeholder="Search by client name" value={query} onChange={e => { setQuery(e.target.value); setCreating(false); }} disabled={busy} /></label>
                <div aria-live="polite" className="crm-match-result-label">{loading ? 'Searching clients…' : shown.length ? 'Possible matches. Choose the right client.' : 'No matching clients found. Try another name or add a new client.'}</div>
                {!loading && <div className="crm-match-results">{shown.map(candidate => {
                    const { company, summary } = candidate;
                    return <button type="button" key={company.id} disabled={busy} className="crm-match-candidate" onClick={() => void choose(company)}>
                        <span className="crm-match-candidate-initial" aria-hidden="true">{company.name.trim().slice(0, 2).toUpperCase()}</span>
                        <span><strong>{company.name}</strong><small>{reasonsOf(candidate) || 'Existing client'}{company.abn ? ` · ABN ${company.abn}` : ''}</small>
                            {summary && <><small>{[summary.contactName, summary.contactEmail].filter(Boolean).join(' · ') || 'No contact added'}</small><small>{summary.quoteCount} {summary.quoteCount === 1 ? 'quote' : 'quotes'} · {summary.siteCount} {summary.siteCount === 1 ? 'site' : 'sites'}</small></>}
                        </span>
                        <ChevronRight size={18} aria-hidden="true" />
                    </button>;
                })}</div>}
                {!loading && unmanaged.length > 0 && <div className="crm-match-unmanaged" role="group" aria-label="Buildings that are not clients">
                    <ul>{unmanaged.map(building => <li key={building.id || building.name}>
                        <strong>Not a client:</strong> {building.name} {unmanagedWhy(building.reasons)}. File it under its firm in {onOpenTidy
                            ? <button type="button" className="crm-match-tidy-link" disabled={busy} onClick={onOpenTidy}>Tidy up</button>
                            : 'Tidy up'}.
                    </li>)}</ul>
                </div>}
                {error && <div role="alert" className="crm-match-error">{error} <button type="button" disabled={busy} onClick={() => errorStage === 'save' ? void choose(lastCandidate.current) : setRetry(n => n + 1)}>{errorStage === 'save' ? 'Retry saving' : 'Retry search'}</button></div>}
                {creating ? <form className="crm-match-create" onSubmit={e => { e.preventDefault(); void choose(); }}>
                    <h3>Create a separate client</h3>
                    <p>Use this when none of the existing records is the same client.</p>
                    <label>Client or company name<input required maxLength={255} value={name} onChange={e => setName(e.target.value)} disabled={busy} /></label>
                    <label>Client type<select value={type} onChange={e => setType(e.target.value)} disabled={busy}>
                        {typeOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select></label>
                    <button type="submit" className="crm-match-primary" disabled={busy || loading || !name.trim() || (!!error && errorStage === 'search')}><Check size={16} />{busy ? 'Saving…' : 'Confirm new client'}</button>
                </form> : <button type="button" className="crm-match-add" onClick={() => setCreating(true)} disabled={busy || loading || !!error}><Plus size={17} />Create a new client</button>}
            </div>
            <div className="crm-match-footer"><span>{busy ? 'Saving your client choice…' : footerHint}</span><button type="button" disabled={busy} onClick={onClose}>{closeLabel}</button></div>
        </div>
    </div>, document.body);
}
